import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { BackToHomeComponent } from '../../components/back-to-home/back-to-home.component';
import { AuthenticationService } from '../../core/services/authentication/authentication.service';
import {RecaptchaV3Service} from '../../core/services/recaptcha/recaptcha-v3-service';
import {TranslateModule} from '@ngx-translate/core';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, RouterModule, BackToHomeComponent, ReactiveFormsModule, TranslateModule],
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit {
  private returnUrl: string;
  loginForm!: FormGroup;
  mfaForm!: FormGroup;
  isLoading = false;
  errorMessage = '';
  requireMfa = false;
  challengeToken = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthenticationService,
    private readonly recaptchaService: RecaptchaV3Service
  ) {
    // Get the return URL from route parameters or default to '/'
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
  }

  ngOnInit(): void {
    this.initForms();

    // Check if there's a stored challenge token that hasn't expired
    const storedToken = this.authService.getChallengeToken();
    if (storedToken) {
      this.challengeToken = storedToken;
      this.requireMfa = true;
    }
  }

  initForms(): void {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      rememberMe: [false]
    });

    this.mfaForm = this.formBuilder.group({
      mfaCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });
  }

  onLoginSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const { email, password, rememberMe } = this.loginForm.value;

    this.recaptchaService.execute('signin',(recaptchaToken) => {
      this.authService.login(recaptchaToken, email, password, rememberMe).subscribe({
        next: (response) => {
          this.isLoading = false;

          if ('isRequireTwoFactor' in response && response.isRequireTwoFactor) {
            this.requireMfa = true;
            this.challengeToken = response.challengeToken;
          } else {
            // Login successful without MFA
            this.router.navigateByUrl(this.returnUrl);
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.message;
        }
      });
    });
  }

  onMfaSubmit(): void {
    if (this.mfaForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const { mfaCode } = this.mfaForm.value;

    this.recaptchaService.execute('signin_mfa',(recaptchaToken) => {
      this.authService.validateMfa(this.challengeToken, mfaCode, recaptchaToken).subscribe({
        next: () => {
          this.isLoading = false;
          this.requireMfa = false;
          this.challengeToken = '';
          this.router.navigateByUrl(this.returnUrl);
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.message;

          // If the challenge token has expired, go back to login form
          if (error.message.includes('Challenge token has expired')) {
            this.requireMfa = false;
            this.challengeToken = '';
          }
        }
      });
    });
  }

  goBack(): void {
    this.router.navigateByUrl(this.returnUrl);
  }

  backToLogin(): void {
    this.requireMfa = false;
    this.challengeToken = '';
    this.errorMessage = '';
  }
}
